import { useState, useRef, useEffect } from "react";

import { css } from "@compiled/react";
import { UniformSlot } from "@uniformdev/canvas-react";

import { type Token } from "@/libraries/design/foundations/token/src/token-types";
import wacToken from "@/libraries/design/foundations/token/src/wac-token";

export interface UniformAnimationWrapperProps {
    animationName: Token;
    duration?: Token;
    easing?: Token;
    delay?: Token;
    fillMode?: "forwards" | "backwards" | "both" | "none";
    trigger?: "load" | "in-view" | "in-view-once";
}

const UniformAnimationWrapper = ({
    animationName,
    duration = "motion.duration.0ms",
    easing = "motion.easing.constant",
    delay = "motion.delay.0ms",
    fillMode = "none",
    trigger = "in-view"
}: UniformAnimationWrapperProps) => {
    const [triggered, setTriggered] = useState(trigger === "load");
    const ref = useRef<HTMLDivElement>(null);
    const isInViewMode = trigger === "in-view" || trigger === "in-view-once";

    useEffect(() => {
        if (!isInViewMode || !ref.current) return;
        const el = ref.current;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry) return;
                if (entry.isIntersecting) {
                    setTriggered(true);
                } else if (trigger === "in-view") {
                    setTriggered(false);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [trigger, isInViewMode]);

    // Restart animation when re-entering view in "in-view" mode so it replays from the beginning
    useEffect(() => {
        if (trigger !== "in-view" || !triggered || !ref.current) return;
        const el = ref.current;
        el.style.animationName = "none";
        el.offsetHeight; // force reflow
        el.style.animationName = "";
    }, [trigger, triggered]);

    const animationStyles = {
        animationName: wacToken(animationName),
        animationDuration: wacToken(duration),
        animationTimingFunction: wacToken(easing),
        animationDelay: wacToken(delay),
        animationFillMode: fillMode,
        animationPlayState: triggered ? "running" : "paused"
    };

    return (
        <div ref={ref} className="animation-wrapper" css={css(animationStyles)}>
            <UniformSlot name="content" />
        </div>
    );
};

export default UniformAnimationWrapper;
